<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import CoverView from '@/views/shared/CoverView.vue';
import { get } from 'lodash';
import { format } from '@/utils/date-fns';
import DescriptionBlock from '@/components/shared/blocks/DescriptionBlock.vue';
import BasicBlock from '@/components/shared/blocks/BasicBlock.vue';
import DepartmentsBlock from '@/components/shared/blocks/DepartmentsBlock.vue';
import i18n from '@/utils/i18n';
import MediaBlock from '@/components/shared/blocks/MediaBlock.vue';
import { useInstitutionStore } from '@/stores/institution';
import { useProfilesStore } from '@/stores/profiles';
import { useCacheStore } from '@/stores/cache';
import { useDepartmentStore } from '@/stores/department';
import constants from '@/utils/constants';
import { getOptimizedImageSrc } from '@/directives/optimizedImageSize';
import IconButton from '@/components/shared/IconButton.vue';
import TitleBlock from '@/components/shared/blocks/TitleBlock.vue';
import { IDepartment, IProfile } from '@/interfaces';
import BlocksWrapper from '@/components/shared/blocks/BlocksWrapper.vue';

const institutionStore = useInstitutionStore();
const profilesStore = useProfilesStore();
const departmentStore = useDepartmentStore();
const cache = useCacheStore();
const route = useRoute();
const router = useRouter();
const isLoading = ref(true);

const profile = computed(
  (): IProfile =>
    profilesStore.all.find((profile) => profile.id === route.params.id) ??
    ({} as IProfile)
);

const myArchive = computed(() => profilesStore.myArchive);

const moduleColor = computed(() => {
  return route?.meta?.color;
});

const shouldRequireMediaAuth = computed(() => {
  if (profile.value.showInfoOnScreen === -1) {
    return institutionStore.settings?.showResInfoDefault;
  }

  return profile.value.showInfoOnScreen === 0;
});

const galleries = computed(() => {
  return get(myArchive.value, 'galleries', []);
});

const pictures = computed(() => {
  return get(myArchive.value, 'pictures', []);
});

const videos = computed(() => {
  return get(myArchive.value, 'videos', []);
});

const documents = computed(() => {
  return get(myArchive.value, 'documents', []);
});

const isEmployee = computed(() => {
  return profile?.value?.type === 'EMPLOYEE' && profile.value?.title;
});

const homeDays = computed(() => {
  const daysTranslated: string[] = [];
  profile.value.homeDays.forEach((day) =>
    daysTranslated.push(i18n.global.t(`global.${day}`))
  );
  return `${i18n.global.t('resident.homeDays')}: ${daysTranslated.join(', ')}`;
});

const coverImage = computed(() => {
  const department: IDepartment | undefined = institutionStore.departments.find(
    (department) => parseInt(department.id) === parseInt(departmentStore.id)
  );

  return department?.presentation?.image || constants.placeholderImage;
});

onMounted(async () => {
  await cache.use('profilesStore.getMyArchive', profilesStore.getMyArchive, {
    id: profile?.value?.id,
    type: profile?.value?.type,
  });

  isLoading.value = false;
});

function getBirthday(day: string) {
  return format(new Date(day), 'd MMMM, y');
}
</script>

<template>
  <div class="profile">
    <CoverView
      v-if="!isLoading && profile.id"
      :image-url="coverImage"
      :module-color="moduleColor"
    >
      <div class="profileContent relative">
        <div
          class="left-1/2 -translate-x-1/2 absolute top-[-132px] flex items-center justify-center"
        >
          <img
            class="w-[264px] h-[264px] rounded-full border-[12px] border-white shadow-bottom bg-green-400 object-cover"
            :src="getOptimizedImageSrc(profile.displayImage, 'small')"
          />
        </div>

        <div>
          <IconButton :icon="['far', 'times']" @click="router.back()" />

          <div
            class="grid grid-cols-[1fr_auto_1fr] items-center h-full w-full mt-4 mb-4"
          >
            <TitleBlock
              class="col-start-2 justify-self-center mb-0"
              :text="profile.displayName"
            />
          </div>

          <div class="flex justify-center mb-4">
            <div
              class="flex items-center justify-center px-3 py-2 bg-neutral-0 rounded-full border-2 border-neutral-200 shadow-smallBold"
            >
              <fa-icon
                class="mr-2"
                :icon="[
                  'fas',
                  profile.type === 'RESIDENT' ? 'fa-user' : 'fa-id-card',
                ]"
              />
              <p class="h500">
                {{ $t(`global.${profile.type.toLowerCase()}`) }}
              </p>
            </div>
          </div>

          <MediaBlock
            :pictures="pictures"
            :videos="videos"
            :documents="documents"
            :galleries="galleries"
            :color="moduleColor as any"
            :needs-auth="shouldRequireMediaAuth"
            :limited-to-profile="shouldRequireMediaAuth ? profile : undefined"
          />

          <BlocksWrapper>
            <BasicBlock v-if="isEmployee" :text="profile.title" :icon="'tag'" />

            <DescriptionBlock
              v-if="profile.description"
              :description="profile.description"
            />

            <BasicBlock
              v-if="profile.showBirthday && profile.birthday"
              :text="getBirthday(profile.birthday)"
              :icon="['fas', 'fa-birthday-cake']"
            />

            <DepartmentsBlock
              v-if="profile.departments.length"
              :departments="profile.departments"
            />

            <!-- Mobile -->
            <BasicBlock
              v-if="profile.mobile"
              :text="profile.mobile"
              :icon="['fas', 'fa-mobile']"
            />

            <!-- Phone -->
            <BasicBlock
              v-if="profile.phone"
              :text="profile.phone"
              :icon="'phone'"
            />

            <!-- Email -->
            <BasicBlock
              v-if="profile.showEmail && profile.email"
              :text="profile.email"
              :icon="['fas', 'fa-envelope']"
            />

            <!-- Home days -->
            <BasicBlock
              v-if="profile.type === 'RESIDENT' && profile.homeDays.length"
              class="capitalize"
              :text="homeDays"
              :icon="['fas', 'fa-street-view']"
            />
          </BlocksWrapper>
        </div>
      </div>
    </CoverView>
  </div>
</template>

<style lang="scss">
.profile {
  .profileContent {
    .media-list {
      padding: 0;

      > *.galleriesMargin {
        margin-bottom: 56px;
      }

      > *.galleries {
        padding: 0;
      }
    }
  }
}
</style>
